import { MODEL_NAMES } from '../constants/index';
import { task } from 'ember-concurrency';
import Service, { service } from '@ember/service';

/**
 * Provides a high-level interface for interacting with the messaging client.
 * Consuming applications should interact with only this service.
 */
export default class MessagingInterfaceService extends Service {
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service('websocket/messagingWebsocket') messagingWebsocket;
  @service store;

  get unreadConversationCount() {
    return this.messaging.currentProfile?.unreadConversationIds?.length || 0;
  }

  get isAway() {
    return this.messaging.currentProfile?.isAway || false;
  }

  /**
   * Initializes and starts the messaging client. This task loads the messaging app
   * and sets up the necessary connections. If a new startTask is triggered while
   * a previous one is running, the previous task will be dropped.
   *
   * @param {Object} [options={}] - Configuration options
   */
  startTask = task({ drop: true }, async (options = {}) => {
    await this.messaging.loadApp.perform(options);
  });

  /**
   * Stops the messaging client by canceling token polling and closing
   * the websocket connection.
   */
  stop() {
    this.messaging.pollMessagingToken.cancelAll();
    this.messagingWebsocket.closeSocket();
  }

  /**
   * Finds or creates a conversation for the given client external IDs and
   * navigates to that conversation.
   *
   * @param {string[]} clientExternalIds - Array of client external IDs to find or create a conversation for
   */
  async goToConversationByClientIds(clientExternalIds) {
    const conversation =
      await this.messaging.findOrCreateConversationByClientIds(clientExternalIds);
    this.messagingNavigation.toConversation(conversation);
  }

  /**
   * Navigates to a conversation by its ID. First attempts to find the conversation
   * in the store. If not found, queries the API for the conversation.
   * If the conversation is found, navigates to it; otherwise navigates to the
   * conversation list.
   *
   * @param {string} conversationId - The ID of the conversation to navigate to
   */
  async goToConversationById(conversationId) {
    let conversation = this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);

    if (!conversation) {
      try {
        const results = await this.store.query(MODEL_NAMES.CONVERSATION, {
          'filter[id]': conversationId,
          include: 'users,last_message,last_message.user',
        });
        conversation = results.firstObject;
      } catch (error) {
        this.messagingLogging.warn(error, { context: 'goToConversationById', conversationId });
        conversation = null;
      }
    }

    return conversation
      ? this.messagingNavigation.toConversation(conversation)
      : this.messagingNavigation.toConversationList();
  }
}
